import React, { useState, useEffect, useRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import { xor, capitalize } from 'lodash';
import { interpolate } from '@format/interpolate';
import { costFormatter } from '@format/currency';
import { Button, IconButton } from '@ui/button';
import { PillText, VisuallyHidden } from '@web/atoms';
import { ResponsiveImage, Carousel } from '@web/molecules';
import {
  IngredientModal,
  useIngredientModal,
  ProductSelector,
  ImageDropdown,
  DropdownImageItem,
} from '@web/components';
import {
  userViewMealPlanTypeScreen,
  userViewPlanDetailsPage,
} from '@web/account/MealPrepTypeSelectionForm/events';
import { useShopifyCart } from '@web/shopify';
import { CMSBlok } from '../../cms';
import { PRODUCT_TYPE_HALF_FRESH } from '../../products/constants';
import { CheckoutBar } from '../CheckoutBar';
import { useReporter, useElevarReporter } from '../../reporter';
import { buildSelectedElevarMealPrepProduct } from '../OnboardingFlowContainer/elevarHelpers';
import { useSessionCustomer } from '../../customer';
import {
  userContinuesFromRecommendedPlan,
  recipeSelected,
  recipeUnselected,
  userExpandsFreshRecipeDetails,
  mealPlanTypeSelected,
} from '../RecommendedPlanForm/events.js';
import { callouts, pillTextProps } from './constants';
import {
  StyledRecommendedPlanContainer,
  StyledForm,
  HeaderContainer,
  Headline,
  FullSubheadline,
  ProductCardContainer,
  Description,
  DesktopImageContainer,
  ContentContainer,
  IconButtonContainer,
  MobileImageContainer,
  StarRating,
  FormSelsectionContainer,
  PricingContainer,
  PriceBeforeDiscount,
  Price,
  DiscountAmount,
  ContinueButtonContainer,
  CalloutsContainer,
  CallloutText,
  CalloutContainer,
  DesktopCheckoutBarContainer,
  MobileCheckoutBarContainer,
  DescriptionContainer,
  MixedDropdownContainer,
  CarouselSectionContainer,
} from './styles';

export const CarouselMealPlanForm = ({
  onSubmit,
  carousel,
  petName,
  headline,
  subheadline,
  description,
  modals,
  ctaText = 'Continue',
  freshRecipes,
  defaultFreshRecipes,
  bakedRecipes,
  defaultBakedRecipes,
  defaultMealPrepType,
  mealPrepTypes,
  unavailableMealPrepTypes,
  collapsedMealPrepTypes,
  onBackButtonClick,
  unavailableFreshRecipes,
  unavailableBakedRecipes,
  period,
  getConfigFromRecipes,
  body,
  notSelectableText,
  ctaButtonVariant = 'yellow',
  isWithinOptimized = false,
}) => {
  const [selectedMealPrepType, setSelectedMealPrepType] =
    useState(defaultMealPrepType);
  const [selectedFreshRecipes, setSelectedFreshRecipes] =
    useState(defaultFreshRecipes);
  const [selectedBakedRecipes, setSelectedBakedRecipes] =
    useState(defaultBakedRecipes);
  const [showBaked, setShowBaked] = useState(false);

  const [showCheckoutBar, setShowCheckoutBar] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const viewEventsFired = useRef(false);
  const elevarViewItemsTracked = useRef(false);

  const reporter = useReporter();
  const elevarReporter = useElevarReporter();

  const { customer, isAppSession } = useSessionCustomer();
  const { cart } = useShopifyCart();
  const customerEmail = cart?.customer?.email || customer?.email;

  const elevarUserProperties = useMemo(() => {
    const props = {};
    if (customerEmail) props.customer_email = customerEmail;
    if (customer?.id) props.customer_id = customer.id;
    return props;
  }, [customerEmail, customer?.id]);

  const allMealPrepProducts = useMemo(
    () => [...(mealPrepTypes || []), ...(collapsedMealPrepTypes || [])],
    [mealPrepTypes, collapsedMealPrepTypes],
  );

  const { modal, onRecipeDetailsClick } = useIngredientModal(modals);

  useEffect(() => {
    if (!viewEventsFired.current) {
      if (isWithinOptimized) {
        reporter.tag(userViewPlanDetailsPage());
      } else {
        reporter.tag(userViewMealPlanTypeScreen());
      }
      viewEventsFired.current = true;
    }
    if (
      selectedMealPrepType === 'fresh' ||
      selectedMealPrepType === 'half_fresh'
    ) {
      setSelectedBakedRecipes([]);
      setShowBaked(false);
    } else {
      setSelectedBakedRecipes(defaultBakedRecipes);
      setShowBaked(true);
    }
  }, [
    reporter,
    cart,
    selectedMealPrepType,
    defaultBakedRecipes,
    isWithinOptimized,
  ]);

  useEffect(() => {
    if (elevarViewItemsTracked.current || !mealPrepTypes?.length) return;
    const normalizedProducts = allMealPrepProducts
      .map(planType =>
        buildSelectedElevarMealPrepProduct({
          mealPrepProducts: allMealPrepProducts,
          mealPrepTypes,
          selectedMealPrepType: planType.value,
          selectedFreshRecipes,
          selectedBakedRecipes,
          getConfigFromRecipes,
        }),
      )
      .filter(Boolean);

    normalizedProducts.forEach(product => {
      elevarReporter?.elevarViewItem({
        planType: product,
        userProperties: elevarUserProperties,
      });
    });
    elevarViewItemsTracked.current = true;
  }, [
    allMealPrepProducts,
    elevarReporter,
    elevarUserProperties,
    selectedFreshRecipes,
    selectedBakedRecipes,
    getConfigFromRecipes,
    mealPrepTypes,
  ]);

  const onFreshChange = freshRecipes => {
    const currentRecipes = selectedFreshRecipes ?? [];
    const recipeAdded = freshRecipes.length > currentRecipes.length;
    const recipe = xor(freshRecipes, currentRecipes)[0];
    const event = recipeAdded ? recipeSelected : recipeUnselected;
    reporter.tag(event(recipe));
    setSelectedFreshRecipes(freshRecipes);
  };

  const onBakedChange = bakedRecipes => {
    const currentRecipes = selectedBakedRecipes ?? [];
    const recipeAdded = bakedRecipes.length > currentRecipes.length;
    const recipe = xor(bakedRecipes, currentRecipes)[0];
    const event = recipeAdded ? recipeSelected : recipeUnselected;
    reporter.tag(event(recipe));
    setSelectedBakedRecipes(bakedRecipes);
  };

  const onMealPrepChange = product => {
    reporter.tag(mealPlanTypeSelected(product[0]));
    if (unavailableMealPrepTypes?.includes(product[0])) return null;
    setSelectedMealPrepType(product[0]);
  };

  const isValid =
    selectedMealPrepType === 'fresh' || selectedMealPrepType === 'half_fresh'
      ? selectedFreshRecipes && selectedFreshRecipes.length > 0
      : selectedFreshRecipes &&
        selectedFreshRecipes.length > 0 &&
        selectedBakedRecipes &&
        selectedBakedRecipes.length > 0;

  const onSubmitHandler = async event => {
    event.preventDefault();
    if (!isValid) {
      return;
    }
    const email = cart?.customer?.email || customer?.email;
    reporter.tag(
      userContinuesFromRecommendedPlan(
        email,
        selectedMealPrepType,
        selectedFreshRecipes,
        selectedBakedRecipes,
      ),
    );

    const selectedProduct = buildSelectedElevarMealPrepProduct({
      mealPrepProducts: allMealPrepProducts,
      mealPrepTypes,
      selectedMealPrepType,
      selectedFreshRecipes,
      selectedBakedRecipes,
      getConfigFromRecipes,
    });
    if (selectedProduct) {
      elevarReporter?.elevarAddToCart({
        product: selectedProduct,
        userProperties: elevarUserProperties,
      });
    }

    setIsSubmitting(true);
    await onSubmit(
      selectedMealPrepType,
      selectedFreshRecipes,
      selectedBakedRecipes,
    );
  };

  const { price, discountedPrice, message, discountAmount } =
    getConfigFromRecipes(
      selectedMealPrepType,
      selectedFreshRecipes,
      selectedBakedRecipes,
    );

  const discountText = Number(discountAmount)
    ? `${Math.trunc(Number(discountAmount))}% off`
    : '';

  const selectedMealPlanContent = mealPrepTypes?.find(
    item => item.value === selectedMealPrepType,
  );

  const checkoutBarProps = {
    ctaText: 'Continue',
    isSubmitDisabled: isSubmitting,
    isBackButtonDisabled: isSubmitting,
    shown: true,
    period,
    isSubmitting: isSubmitting,
    relativePosition: isAppSession,
    price,
    discountedPrice,
    savePercentText: discountText,
    message,
    buttonVariant: ctaButtonVariant,
    headline: `${capitalize(petName)}'s ${selectedMealPlanContent?.title} Plan`,
    withImage: true,
    image: carousel?.[0],
  };

  const onRecipeDetailsHandler = product => {
    reporter.tag(userExpandsFreshRecipeDetails(product.id));
    if (product?.type === 'baked') {
      onRecipeDetailsClick('baked_' + product.id);
    } else {
      onRecipeDetailsClick(product.id);
    }
  };

  const getConfiguredPlanTypes = planTypes =>
    planTypes?.map(planType => ({
      ...planType,
      shouldShowMoreDetails: false,
      inputType: 'radio',
    }));

  const mealPrepSelectorProps = {
    selected: [selectedMealPrepType],
    unavailableProducts: unavailableMealPrepTypes,
    variant: 'radio',
    direction: 'horizontal',
    cardOrientation: 'column',
    notSelectableText: notSelectableText,
    productCardBackgroundColor: '',
  };

  useEffect(() => {
    const element = document.getElementById('continueButton');

    //show the checkout bar when the top of the continue button has passed the top of the veiwpot
    const handleScroll = () => {
      const { top } = element.getBoundingClientRect();

      if (top <= 0) {
        setShowCheckoutBar(true);
      } else {
        setShowCheckoutBar(false);
      }
    };

    window.addEventListener('scroll', handleScroll);

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const getDropdownSelector = type => {
    const dropdownTypeProps = {
      fresh: {
        helperText: '(Choose up to 4)',
        defaultSelectedIds: selectedFreshRecipes,
        onChange: onFreshChange,
        recipes: freshRecipes,
        unavailableRecipes: unavailableFreshRecipes,
        maxSelections: 4,
        imageSrc: freshRecipes?.[0].img.src,
      },
      baked: {
        helperText: '(Choose up to 2)',
        defaultSelectedIds: selectedBakedRecipes,
        onChange: onBakedChange,
        recipes: bakedRecipes,
        unavailableRecipes: unavailableBakedRecipes,
        maxSelections: 2,
        imageSrc: bakedRecipes?.[0].img.src,
      },
    };

    const props = {
      heading: "Bella's recipes",
      helperText: '(Choose up to 4)',
      altText: 'Recipe Selection Box',
      defaultText: 'Select your proteins',
      onDetailsClick: onRecipeDetailsHandler,
      ...dropdownTypeProps[type],
    };

    return (
      <ImageDropdown {...props}>
        {props.recipes?.map(recipe => (
          <DropdownImageItem
            key={recipe.value}
            id={recipe.value}
            heading={`${capitalize(
              recipe.value,
            )} ${recipe.description.toLowerCase()}`}
            summaryLabel={recipe.value}
            imageSrc={recipe.img.src}
            altText={recipe.img.alt}
            linkText="View Ingredients"
            type={type}
            isAllergen={props.unavailableRecipes?.includes(recipe.value)}
          />
        ))}
      </ImageDropdown>
    );
  };

  const bakedSelected = selectedMealPrepType === 'baked';
  const freshSelected =
    selectedMealPrepType === 'fresh' || selectedMealPrepType === 'half_fresh';
  const mixedSelected = selectedMealPrepType === 'mixed';

  const requiredItemsNotSelected =
    (bakedSelected && !selectedBakedRecipes.length) ||
    (freshSelected && !selectedFreshRecipes.length) ||
    (mixedSelected &&
      (!selectedBakedRecipes.length || !selectedFreshRecipes.length));

  return (
    <StyledForm onSubmit={onSubmitHandler}>
      <DesktopCheckoutBarContainer $show={showCheckoutBar}>
        <CheckoutBar
          {...checkoutBarProps}
          top={true}
          topPosition={10}
          isSubmitDisabled={isSubmitting || requiredItemsNotSelected}
          isSubmitting={isSubmitting}
        />
      </DesktopCheckoutBarContainer>

      <StyledRecommendedPlanContainer>
        <IconButtonContainer aria-live="polite">
          <IconButton
            aria-label="go to previous step"
            icon={{
              iconName: 'arrow-left',
              size: '24px',
              alt: 'back',
              id: 'prev_step_footer',
              color: 'hero.plum',
            }}
            onClick={onBackButtonClick}
            type="button"
            variant="lilac"
          />
        </IconButtonContainer>
        {Boolean(carousel?.length) && (
          <CarouselSectionContainer>
            <DesktopImageContainer>
              <Carousel
                showDots={false}
                withThumbnails={true}
                showArrows={false}
                borderRadius={24}
                thumbBorderRadius={16}
                verticalPadding={0}
                thumbAlign="start"
                thumbBorderColor="secondary.lilac"
              >
                {carousel?.map((item, index) => (
                  <ResponsiveImage {...item} key={index} />
                ))}
              </Carousel>
            </DesktopImageContainer>
            <MobileImageContainer>
              <Carousel
                showDots={false}
                showArrows={false}
                borderRadius={32}
                verticalPadding={0}
                thumbAlign="start"
                slideFullWidth={true}
                showPartialSlideMobile={true}
                itemsToShow={2}
              >
                {carousel?.map((item, index) => (
                  <ResponsiveImage {...item} key={index} />
                ))}
              </Carousel>
            </MobileImageContainer>
          </CarouselSectionContainer>
        )}
        <ContentContainer>
          <HeaderContainer>
            <Headline>{headline}</Headline>
            <ProductSelector
              gridColumns={2}
              stackOnMobile={false}
              products={getConfiguredPlanTypes(mealPrepTypes)}
              unavailableProducts={unavailableMealPrepTypes}
              onChange={onMealPrepChange}
              {...mealPrepSelectorProps}
              productCardVariant="simplifiedCro"
              gapSize={2}
            />
          </HeaderContainer>
          <DescriptionContainer>
            <FullSubheadline>
              {interpolate(subheadline, {
                pupName: capitalize(petName),
                planType:
                  selectedMealPrepType === PRODUCT_TYPE_HALF_FRESH
                    ? 'half fresh'
                    : selectedMealPrepType,
              })}
            </FullSubheadline>
            <PillText
              text={
                selectedMealPlanContent?.benefit ||
                pillTextProps[selectedMealPrepType]?.text
              }
              variant="success"
              wiggle={true}
            />
            <StarRating>
              {' '}
              20K+ 5-star reviews
              <VisuallyHidden>{5} star rating for Ollie</VisuallyHidden>
            </StarRating>
            <Description>{description}</Description>
            {discountedPrice && discountText ? (
              <PricingContainer>
                <PriceBeforeDiscount>${price}</PriceBeforeDiscount>
                <Price>
                  {costFormatter(discountedPrice)}/{period}
                </Price>
                <DiscountAmount>{discountText}</DiscountAmount>
              </PricingContainer>
            ) : (
              <PricingContainer>
                <Price>${price}</Price>
              </PricingContainer>
            )}
          </DescriptionContainer>
          <ProductCardContainer>
            <FormSelsectionContainer>
              {selectedMealPrepType === 'fresh' ||
              selectedMealPrepType === 'half_fresh' ? (
                getDropdownSelector('fresh')
              ) : (
                <MixedDropdownContainer>
                  {getDropdownSelector('fresh')}
                  {showBaked && getDropdownSelector('baked')}
                </MixedDropdownContainer>
              )}
            </FormSelsectionContainer>

            <IngredientModal {...modal} />
          </ProductCardContainer>
          <ContinueButtonContainer>
            <Button
              type="button"
              disabled={isSubmitting || requiredItemsNotSelected}
              variant={ctaButtonVariant}
              isLoading={isSubmitting}
              percentWidthMobile={80}
              horizontalPadding={32}
              id="continueButton"
            >
              {ctaText}
            </Button>
          </ContinueButtonContainer>
          <CalloutsContainer>
            {callouts?.map(({ icon, text }) => (
              <CalloutContainer key={text}>
                <ResponsiveImage
                  src={icon.src}
                  alt={`${text} icon`}
                  width={icon.width}
                  height={icon.height}
                />
                <CallloutText>{text}</CallloutText>
              </CalloutContainer>
            ))}
          </CalloutsContainer>
        </ContentContainer>
      </StyledRecommendedPlanContainer>

      {body?.map(content => (
        <CMSBlok key={content._uid} content={content} />
      ))}

      <MobileCheckoutBarContainer $show={showCheckoutBar}>
        <CheckoutBar
          {...checkoutBarProps}
          isSubmitDisabled={isSubmitting || requiredItemsNotSelected}
          isSubmitting={isSubmitting}
        />
      </MobileCheckoutBarContainer>
    </StyledForm>
  );
};
CarouselMealPlanForm.displayName = 'CarouselMealPlanForm';
CarouselMealPlanForm.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  carousel: PropTypes.arrayOf(PropTypes.object.isRequired),
  headline: PropTypes.string.isRequired,
  subheadline: PropTypes.string,
  description: PropTypes.string.isRequired,
  petName: PropTypes.string.isRequired,
  modals: PropTypes.array,
  ctaText: PropTypes.string.isRequired,
  freshRecipes: PropTypes.array,
  defaultFreshRecipes: PropTypes.array,
  bakedRecipes: PropTypes.array,
  defaultBakedRecipes: PropTypes.array,
  mealPrepTypes: PropTypes.array,
  defaultMealPrepTypes: PropTypes.array,
  onBackButtonClick: PropTypes.func.isRequired,
  unavailableFreshRecipes: PropTypes.array,
  unavailableBakedRecipes: PropTypes.array,
  unavailableMealPrepTypes: PropTypes.array,
  collapsedMealPrepTypes: PropTypes.array,
  period: PropTypes.string.isRequired,
  body: PropTypes.array,
  getConfigFromRecipes: PropTypes.func.isRequired,
  notSelectableText: PropTypes.string,
  hideHalfFresh: PropTypes.bool,
  showFreshOnly: PropTypes.bool,
  hideMixed: PropTypes.bool,
  ctaButtonVariant: PropTypes.string,
  isWithinOptimized: PropTypes.bool,
};
